import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from '@tanstack/react-router'
import { requireAnyPermission } from '@/lib/permissions/permissions'
import StatusBadge from '@/components/statusBadge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { ClientBreadcrumbSwitcher } from '@/components/client/ClientBreadcrumbSwitcher'
import {
  Home,
  ListTodo,
  MessageCircle,
  MoreHorizontal,
  Slash,
  Dumbbell,
  SendHorizonal,
  StickyNote,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react'
import * as luxon from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavbar } from '@/components/navbar'
import { cn } from '@/lib/utils/ui'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import ClientLoadingState from '@/components/client/ClientLoadingState'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Kbd } from '@/components/ui/kbd'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import SendInviteClientEmailDialog from '@/components/client/SendInviteClientEmailDialog'
import { useSupabaseSession, useUserId } from '@/lib/supabase/supabaseProvider'
import { createConversationWithClient } from '@/lib/utils/conversationUtils'
import { toast } from 'sonner'
import StartWorkoutDialog from '@/components/workouts/StartWorkoutDialog'
import DemoModeDialog from '@/components/client/DemoModeDialog'
import ComponentGate from '@/components/ComponentGate'
import { ClientConversationPanel } from '@/components/chat/ClientConversationPanel'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useIsMobile } from '@/hooks/use-mobile'
import { useVersionHistoryHost } from '@/components/planHistory/VersionHistoryHost'
import { useMediaQuery } from '@/lib/hooks/useMediaQuery'
import { useDocumentTitle } from '@/lib/hooks/useDocumentTitle'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { trpc } from '@/lib/trpc/client'
import {
  ClientNotesTasksPanel,
  type ClientNotesTasksTab,
} from '@/components/client/ClientNotesTasksSheet'

export const Route = createFileRoute(
  '/_auth/_dashboard/clients/$clientId/_layout',
)({
  beforeLoad: () => {
    requireAnyPermission(['clients.read_all', 'clients.read_assigned'])
  },
  component: ClientView,
  pendingComponent: () => <ClientLoadingState />,
  errorComponent: ({ error }) => <ClientFetchErrorState error={error} />,
})

interface Tab {
  name: string
  path: string
  featureFlag?: string
}

const tabs: Tab[] = [
  { name: 'Overview', path: '' },
  { name: 'Roadmap', path: 'roadmap' },
  { name: 'Check-in', path: 'checkin' },
  { name: 'Nutrition', path: 'nutrition' },
  { name: 'Training', path: 'training' },
  { name: 'Activity', path: 'activity' },
  { name: 'Habits', path: 'habits' },
  { name: 'Metrics', path: 'metrics' },
  { name: 'Gallery', path: 'gallery' },
  { name: 'Vault', path: 'vault' },
  { name: 'Settings', path: 'settings' },
]

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName.toLowerCase()
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    target.isContentEditable
  )
}

function ClientView() {
  const { setContent } = useNavbar()
  const { clientId } = Route.useParams()
  const userId = useUserId()
  const { session } = useSupabaseSession()
  const {
    data: client,
    error: clientError,
    refetch: refetchClient,
  } = trpc.clients.get.useQuery(
    { clientId },
    { enabled: Boolean(clientId) },
  )

  const { pathname } = useLocation()
  const isMobile = useIsMobile()
  const useFloatingChatPanel = useMediaQuery('(max-width: 1199px)')
  const versionHistory = useVersionHistoryHost()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [notesSheetOpen, setNotesSheetOpen] = useState(false)
  const [notesPanelTab, setNotesPanelTab] =
    useState<ClientNotesTasksTab>('notes')
  const [workoutDialogOpen, setWorkoutDialogOpen] = useState(false)
  const [isCreatingConversation, setIsCreatingConversation] = useState(false)
  const [chatPanelOpen, setChatPanelOpen] = useState(false)
  const [chatSheetOpen, setChatSheetOpen] = useState(false)
  const utils = trpc.useUtils()
  const initials = client
    ? `${client.first_name?.[0] ?? ''}${client.last_name?.[0] ?? ''}`
    : ''

  const avatarUrl = client?.avatar?.url
  const displayEmail = client?.is_demo
    ? (session.user.email ?? client.email)
    : client?.email
  const clientName = client
    ? `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim()
    : ''

  useDocumentTitle(clientName || displayEmail || 'Client')

  const coachEmail = session?.user?.email ?? 'your email'
  const timeZoneLabel = client?.timezone ?? 'UTC'
  const resolvedClientCategory =
    client?.client_category === 'online' ||
    client?.client_category === 'in-person' ||
    client?.client_category === 'hybrid'
      ? client.client_category
      : null
  const showWorkoutButton =
    client?.client_category === 'hybrid' ||
    client?.client_category === 'in-person'
  const [localTimeLabel, setLocalTimeLabel] = useState<string | null>(null)

  useEffect(() => {
    let timerId: ReturnType<typeof setInterval> | null = null

    const updateLocalTime = () => {
      try {
        const zonedNow = luxon.DateTime.now().setZone(timeZoneLabel)
        if (!zonedNow.isValid) {
          setLocalTimeLabel(null)
          return
        }
        setLocalTimeLabel(zonedNow.toFormat('h:mma').toLowerCase())
      } catch {
        setLocalTimeLabel(null)
      }
    }

    updateLocalTime()
    timerId = setInterval(updateLocalTime, 60000)

    return () => {
      if (timerId) {
        clearInterval(timerId)
      }
    }
  }, [timeZoneLabel])

  const {
    data: selectedConversation,
    isLoading: isConversationsLoading,
  } = trpc.chat.getDirectConversation.useQuery(
    { clientUserId: clientId },
    { enabled: Boolean(clientId) },
  )
  const conversationId = selectedConversation?.conversation_id ?? null

  const breadcrumb = useMemo(
    () =>
      client ? (
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink to="/clients">Clients</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>
              <Slash />
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <ClientBreadcrumbSwitcher
                clientId={clientId}
                clientName={clientName || 'Client'}
              />
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      ) : null,
    [client, clientId, clientName],
  )

  useEffect(() => {
    setContent(breadcrumb)
    return () => setContent(null)
  }, [breadcrumb, setContent])

  useEffect(() => {
    if (!versionHistory?.open) return
    setChatPanelOpen(false)
    setChatSheetOpen(false)
    setNotesSheetOpen(false)
  }, [versionHistory?.open])

  const handleChatClick = useCallback(async () => {
    if (isCreatingConversation || isConversationsLoading) return
    versionHistory?.setOpen(false)
    setNotesSheetOpen(false)
    if (conversationId) {
      setChatPanelOpen(true)
      setChatSheetOpen(isMobile)
      return
    }

    if (!userId) {
      toast.error('Unable to start chat right now')
      return
    }

    setIsCreatingConversation(true)
    try {
      await createConversationWithClient({
        clientId,
      })
      await utils.chat.getDirectConversation.cancel({ clientUserId: clientId })
      await utils.chat.getDirectConversation.fetch({
        clientUserId: clientId,
      })
      setChatPanelOpen(true)
      setChatSheetOpen(isMobile)
      void utils.chat.listConversations.invalidate()
    } catch (err) {
      console.error('Failed to create conversation', err)
      toast.error('Failed to start conversation')
    } finally {
      setIsCreatingConversation(false)
    }
  }, [
    clientId,
    conversationId,
    isConversationsLoading,
    isCreatingConversation,
    isMobile,
    userId,
    utils.chat.getDirectConversation,
    utils.chat.listConversations,
    versionHistory,
  ])

  const openNotesPanel = useCallback((tab: ClientNotesTasksTab) => {
    versionHistory?.setOpen(false)
    setChatPanelOpen(false)
    setChatSheetOpen(false)
    setNotesPanelTab(tab)
    setNotesSheetOpen(true)
  }, [versionHistory])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) {
        return
      }
      if (isEditableTarget(event.target)) return

      const key = event.key.toLowerCase()
      if (key === 'c') {
        event.preventDefault()
        void handleChatClick()
        return
      }
      if (key === 'n') {
        event.preventDefault()
        openNotesPanel('notes')
        return
      }
      if (key === 't') {
        event.preventDefault()
        openNotesPanel('tasks')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleChatClick, openNotesPanel])

  if (clientError) {
    return (
      <ClientFetchErrorState
        error={clientError}
        onRetry={() => void refetchClient()}
      />
    )
  }
  if (!client) return <ClientLoadingState />

  const closeNotesPanel = () => {
    setNotesSheetOpen(false)
  }
  const notesPanel = (
    <ClientNotesTasksPanel
      clientId={clientId}
      tab={notesPanelTab}
      onTabChange={setNotesPanelTab}
      onClose={closeNotesPanel}
    />
  )
  const sidePanelOpen = (chatPanelOpen || notesSheetOpen) && !isMobile
  const chatPanel = selectedConversation ? (
    <ClientConversationPanel
      conversation={selectedConversation}
      userId={userId}
      clientId={clientId}
      onClose={() => {
        setChatPanelOpen(false)
        setChatSheetOpen(false)
      }}
      showProfileLink={false}
    />
  ) : null

  return (
    <>
      <SendInviteClientEmailDialog
        clientId={clientId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
      <StartWorkoutDialog
        clientId={clientId}
        clientName={`${client.first_name} ${client.last_name}`}
        open={workoutDialogOpen}
        onOpenChange={setWorkoutDialogOpen}
      />
      <div
        className={cn(
          'flex min-h-0 w-full items-start',
          sidePanelOpen && 'client-layout-chat-open',
        )}
      >
        <div
          className={cn(
            'min-w-0 flex-1 transition-all duration-300',
            sidePanelOpen ? 'xl:pr-0' : '',
          )}
        >
        <div className="mx-auto mt-2 flex min-h-0 w-full flex-col border-b text-card-foreground sm:mt-4 md:w-[90%]">
          {/* Top Content */}

          <div className="flex w-full items-start gap-3 px-4 py-3 md:px-0 md:pb-3">
            <div className="flex shrink-0 flex-col items-center gap-1.5 sm:items-start">
              <Avatar className="h-14 w-14 rounded-md border">
                <AvatarImage
                  src={avatarUrl}
                  className="object-cover w-auto h-auto"
                />
                <AvatarFallback className="rounded-md">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="sm:hidden">
                <StatusBadge
                  status={client.status}
                  clientCategory={resolvedClientCategory}
                />
              </div>
            </div>
            <div className="flex min-w-0 flex-1 items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="min-w-0 flex-1 truncate text-xl font-bold leading-snug sm:flex-none sm:overflow-visible sm:whitespace-normal sm:text-2xl">{`${client.first_name} ${client.last_name}`}</h4>
                  <div className="hidden items-center gap-2 sm:flex">
                    <StatusBadge
                      status={client.status}
                      clientCategory={resolvedClientCategory}
                    />
                    {client.is_demo && (
                      <DemoModeDialog
                        coachEmail={coachEmail}
                        clientId={clientId}
                      />
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="-mr-2 ml-auto h-8 w-8 shrink-0 sm:hidden"
                        aria-label="More actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {showWorkoutButton && (
                        <DropdownMenuItem
                          onClick={() => setWorkoutDialogOpen(true)}
                        >
                          <Dumbbell />
                          Workout
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={handleChatClick}
                        disabled={
                          isCreatingConversation || isConversationsLoading
                        }
                      >
                        <MessageCircle />
                        {isCreatingConversation ? 'Starting…' : 'Chat'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openNotesPanel('notes')}>
                        <StickyNote />
                        Notes
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openNotesPanel('tasks')}>
                        <ListTodo />
                        Tasks
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDialogOpen(true)}>
                        <SendHorizonal />
                        Send invite
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <p className="mt-1.5 truncate text-sm leading-snug text-muted-foreground">
                  {displayEmail}
                </p>
                {localTimeLabel && (
                  <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-sm leading-snug text-muted-foreground">
                    <Home className="h-3.5 w-3.5 shrink-0" />
                    <span
                      className="truncate"
                      title={`${localTimeLabel} - ${timeZoneLabel}`}
                    >
                      {localTimeLabel} – {timeZoneLabel}
                    </span>
                  </div>
                )}
              </div>
              <div className="hidden shrink-0 items-center justify-end gap-2 sm:flex">
                <TooltipProvider delayDuration={200}>
                  <div className="flex items-center gap-2">
                    {showWorkoutButton && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Workout"
                            onClick={() => setWorkoutDialogOpen(true)}
                          >
                            <Dumbbell className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Workout</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={
                            isCreatingConversation ? 'Starting chat' : 'Chat'
                          }
                          onClick={handleChatClick}
                          disabled={
                            isCreatingConversation || isConversationsLoading
                          }
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="flex items-center gap-2">
                        {isCreatingConversation ? 'Starting…' : 'Chat'}
                        <Kbd>C</Kbd>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Notes"
                          onClick={() => openNotesPanel('notes')}
                        >
                          <StickyNote className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="flex items-center gap-2">
                        Notes
                        <Kbd>N</Kbd>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Tasks"
                          onClick={() => openNotesPanel('tasks')}
                        >
                          <ListTodo className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="flex items-center gap-2">
                        Tasks
                        <Kbd>T</Kbd>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Send invite"
                          onClick={() => setDialogOpen(true)}
                        >
                          <SendHorizonal className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Send invite</TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </div>
            </div>
          </div>

          {/* Tab bar */}
          <ScrollArea>
            <div className="w-max flex px-2 md:px-0">
              {tabs.map((tab) => {
                const selected = isTabSelected(tab, pathname)
                return (
                  <ComponentGate
                    key={tab.name}
                    flag={tab.featureFlag}
                    trackInteraction={false}
                  >
                    <Link
                      className={cn(
                        selected && 'border-b-2 border-blue-700',
                        'hover:text-blue-700 p-2',
                      )}
                      to={'/clients/$clientId/' + tab.path}
                      params={{ clientId }}
                      resetScroll={false}
                    >
                      {tab.name}
                    </Link>
                  </ComponentGate>
                )
              })}
            </div>
            <ScrollBar orientation="horizontal" className="hidden" />
          </ScrollArea>
        </div>
        <div className="mt-4">
          <Outlet />
        </div>
        </div>
        {!isMobile && !useFloatingChatPanel && chatPanelOpen ? (
          <aside className="ph-no-capture sticky top-0 hidden h-[calc(100svh-4rem)] w-[520px] shrink-0 animate-in slide-in-from-right border-l md:block">
            {chatPanel ?? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Loading chat...
              </div>
            )}
          </aside>
        ) : null}
        {!isMobile && notesSheetOpen ? (
          <aside className="sticky top-0 hidden h-[calc(100svh-4rem)] w-[420px] shrink-0 animate-in slide-in-from-right overflow-hidden border-l md:block">
            {notesPanel}
          </aside>
        ) : null}
      </div>
      {!isMobile && useFloatingChatPanel && chatPanelOpen ? (
        <div className="ph-no-capture fixed inset-y-4 right-4 z-40 w-[min(520px,calc(100vw-2rem))] overflow-hidden rounded-md border bg-background shadow-xl animate-in slide-in-from-right">
          {chatPanel ?? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Loading chat...
            </div>
          )}
        </div>
      ) : null}
      {isMobile && notesSheetOpen ? (
        <Sheet open={notesSheetOpen} onOpenChange={setNotesSheetOpen}>
          <SheetContent
            side="bottom"
            className="h-svh gap-0 rounded-none p-0"
            hideClose
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Client notes and tasks</SheetTitle>
              <SheetDescription>
                Notes and tasks for this client.
              </SheetDescription>
            </SheetHeader>
            {notesPanel}
          </SheetContent>
        </Sheet>
      ) : null}
      {isMobile && chatPanelOpen ? (
        <Sheet
          open={chatSheetOpen}
          onOpenChange={(open) => {
            setChatSheetOpen(open)
            if (!open) {
              setChatPanelOpen(false)
            }
          }}
        >
            <SheetContent
              side="bottom"
              className="ph-no-capture h-svh gap-0 rounded-none p-0"
              hideClose
            >
              <SheetHeader className="sr-only">
                <SheetTitle>Client chat</SheetTitle>
                <SheetDescription>
                  Chat with this client without leaving their profile.
                </SheetDescription>
              </SheetHeader>
              {chatPanel ?? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Loading chat...
                </div>
              )}
            </SheetContent>
          </Sheet>
      ) : null}
    </>
  )
}

function ClientFetchErrorState({
  error,
  onRetry,
}: {
  error?: unknown
  onRetry?: () => void
}) {
  const description = getClientFetchErrorDescription(error)

  return (
    <div className="mx-auto flex min-h-[50vh] w-full max-w-2xl items-center justify-center px-4 py-10">
      <Card className="w-full">
        <CardHeader className="space-y-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="space-y-1">
            <CardTitle>Unable to load this client</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={onRetry ?? (() => window.location.reload())}>
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
          <Button asChild variant="outline">
            <Link to="/clients">Back to clients</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

function getClientFetchErrorDescription(error: unknown) {
  if (error instanceof Error && error.message) {
    const normalizedMessage = error.message.toLowerCase()

    if (
      normalizedMessage.includes('json object requested') ||
      normalizedMessage.includes('no rows') ||
      normalizedMessage.includes('forbidden') ||
      normalizedMessage.includes('not found')
    ) {
      return 'This client may have been removed, archived, or is no longer available to your account.'
    }
  }

  return 'We could not load this client right now. Check your connection and try again.'
}

function isTabSelected(tab: Tab, pathname: string) {
  if (!tab.path.length) {
    // string starts with /, so e.g /clients/id/path
    // overview has an empty path so there will be 3 parts
    return pathname.split('/').length <= 3
  }

  // Split the pathname to get individual segments
  const pathSegments = pathname.split('/').filter(Boolean)
  const clientIdIndex = pathSegments.findIndex(
    (segment) => segment === 'clients',
  )

  if (clientIdIndex === -1) return false

  // Get the path after the client ID (clientIdIndex + 2 because we have "clients" and the actual clientId)
  const pathAfterClient = pathSegments.slice(clientIdIndex + 2)

  if (pathAfterClient.length === 0) {
    // We're at the client root, only overview should be selected
    return tab.path === ''
  }

  return pathAfterClient[0] === tab.path
}
